import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import React from "react";
const ROW_HEIGHT = 44;
const VISIBLE_ROWS = 3;
const EDGE_PADDING = (ROW_HEIGHT * (VISIBLE_ROWS - 1)) / 2;
// Bound the scroll surface even for the contract's maximum integer quantity.
const WINDOW_SIZE = 101;
export default function QuantityWheel({ maximum, value, onChange, disabled }) {
  const wheel = React.useRef(null);
  const labelId = React.useId();
  const [start, setStart] = React.useState(1);
  const count = Math.min(maximum, WINDOW_SIZE);
  const recenterTimer = React.useRef(null);
  const expectedScroll = React.useRef(null);
  const choose = (next) => onChange(Math.max(1, Math.min(maximum, next)));
  React.useLayoutEffect(() => {
    const node = wheel.current;
    const offset = (value - start) * ROW_HEIGHT;
    if (value < start || value >= start + count) {
      setStart(
        Math.max(
          1,
          Math.min(maximum - count + 1, value - Math.floor(count / 2)),
        ),
      );
    } else if (Math.abs(node.scrollTop - offset) > 1) {
      expectedScroll.current = offset;
      node.scrollTop = offset;
    }
  }, [value, start, count, maximum]);
  React.useEffect(() => () => clearTimeout(recenterTimer.current), []);
  return (
    <Box
      className="quantity-picker"
      style={{
        "--wheel-row-height": `${ROW_HEIGHT}px`,
        "--wheel-padding": `${EDGE_PADDING}px`,
      }}
    >
      <span id={labelId}>Copies to remove</span>
      <Paper className="quantity-wheel-frame">
        <Box
          className="quantity-wheel-selection"
          aria-hidden="true"
          sx={{ bgcolor: "action.selected", borderRadius: 1 }}
        />
        <Box
          ref={wheel}
          className="quantity-wheel"
          role="spinbutton"
          tabIndex={disabled ? -1 : 0}
          aria-labelledby={labelId}
          aria-valuemin={1}
          aria-valuemax={maximum}
          aria-valuenow={value}
          aria-valuetext={`${value} ${value === 1 ? "copy" : "copies"} to remove`}
          aria-disabled={disabled}
          style={{
            height: ROW_HEIGHT * VISIBLE_ROWS,
            overflowY: disabled ? "hidden" : "auto",
          }}
          onKeyDown={(event) => {
            if (disabled) return;
            const next = {
              ArrowUp: value - 1,
              ArrowDown: value + 1,
              PageUp: value - 5,
              PageDown: value + 5,
              Home: 1,
              End: maximum,
            }[event.key];
            if (next !== undefined) {
              event.preventDefault();
              choose(next);
            }
          }}
          onScroll={(event) => {
            if (disabled) return;
            const offset = event.currentTarget.scrollTop;
            if (expectedScroll.current !== null) {
              const expected = expectedScroll.current;
              expectedScroll.current = null;
              if (Math.abs(offset - expected) < 1) return;
            }
            const selected = Math.max(
              start,
              Math.min(
                start + count - 1,
                start + Math.round(offset / ROW_HEIGHT),
              ),
            );
            choose(selected);
            clearTimeout(recenterTimer.current);
            recenterTimer.current = setTimeout(() => {
              if (
                count < maximum &&
                (selected <= start + 2 || selected >= start + count - 3)
              ) {
                setStart(
                  Math.max(
                    1,
                    Math.min(
                      maximum - count + 1,
                      selected - Math.floor(count / 2),
                    ),
                  ),
                );
              }
            }, 140);
          }}
        >
          <Box
            aria-hidden="true"
            style={{
              height: EDGE_PADDING,
            }}
          />
          {Array.from(
            {
              length: count,
            },
            (_, index) => start + index,
          ).map((number) => (
            <Box
              key={number}
              aria-hidden="true"
              className={`quantity-wheel-number${number === value ? " selected" : ""}`}
              sx={
                number === value
                  ? { color: "text.primary", fontWeight: 700 }
                  : undefined
              }
              style={{
                height: ROW_HEIGHT,
              }}
              onClick={() => {
                if (!disabled) {
                  wheel.current.focus();
                  choose(number);
                }
              }}
            >
              {number}
            </Box>
          ))}
          <Box
            aria-hidden="true"
            style={{
              height: EDGE_PADDING,
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
